// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { Dispatch, TransitionEvent, useEffect } from 'react';
import { AnyAction } from 'redux';
import { connect } from 'react-redux';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Tabs from 'antd/lib/tabs';
import Layout from 'antd/lib/layout';

import { CombinedState } from 'reducers';
import { DimensionType } from 'cvat-core-wrapper';
import LabelsList from 'components/annotation-page/standard-workspace/objects-side-bar/labels-list';
import { collapseSidebar as collapseSidebarAction } from 'actions/annotation-actions';
import AppearanceBlock from 'components/annotation-page/appearance-block';
import IssuesListComponent from 'components/annotation-page/standard-workspace/objects-side-bar/issues-list';
import StepsPanel from 'components/annotation-page/workspace/steps/steps-panel';
import { useStepsContext } from 'components/annotation-page/workspace/steps/steps-context';
import ReferencesPanel from 'components/annotation-page/workspace/references/references-panel';
import { useReferencesContext } from 'components/annotation-page/workspace/references/references-context';
import PreviewPanelContainer from 'components/annotation-page/workspace/preview-panel-container';
import { usePreviewMode } from 'components/annotation-page/workspace/preview-mode';
import ScenePanel from 'components/annotation-page/workspace/scene/scene-panel';
import { useSceneContext } from 'components/annotation-page/workspace/scene/scene-context';
import ObjectsListContainer from 'containers/annotation-page/standard-workspace/objects-side-bar/objects-list';
import { t } from 'utils/i18n';

interface OwnProps {

}

interface StateToProps {
    sidebarCollapsed: boolean;
    jobInstance: any;
    issuesCount: number;
}

interface DispatchToProps {
    collapseSidebar(): void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            sidebarCollapsed,
            job: { instance: jobInstance },
        },
        review: { issues },
    } = state;

    return {
        sidebarCollapsed,
        jobInstance,
        issuesCount: issues.length,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchToProps {
    return {
        collapseSidebar(): void {
            dispatch(collapseSidebarAction());
        },
    };
}

export function ObjectsSideBar(props: StateToProps & DispatchToProps & OwnProps): JSX.Element {
    const {
        sidebarCollapsed, collapseSidebar, jobInstance, issuesCount,
    } = props;

    const collapse = (): void => {
        const [collapser] = window.document.getElementsByClassName('cvat-objects-sidebar');
        const listener = (event: TransitionEvent): void => {
            if (event.target && event.propertyName === 'width' && event.target === collapser) {
                window.dispatchEvent(new Event('resize'));
                (collapser as HTMLElement).removeEventListener('transitionend', listener as any);
            }
        };

        if (collapser) {
            (collapser as HTMLElement).addEventListener('transitionend', listener as any);
        }

        collapseSidebar();
    };

    // task jobs and the photos of a task's references get the Steps tab; the legacy reference page does not
    const hasSteps = useStepsContext() !== null;
    const references = useReferencesContext();
    const preview = usePreviewMode();
    // a mode switch remounts the tabs on their default one, so Превью is left
    useEffect(() => { preview?.setActive(false); }, [references]);
    // Steps comes first and opens first wherever it is shown, then the photo's detections
    let defaultTab = 'objects';
    if (hasSteps) defaultTab = 'steps';
    else if (references) defaultTab = 'detections';
    const is2D = jobInstance ? jobInstance.dimension === DimensionType.DIMENSION_2D : true;
    // the header's Scene button shows the panel here, in place of the tabs
    const sceneOpen = !!useSceneContext()?.open;
    useEffect(() => {
        if (sceneOpen && sidebarCollapsed) collapse();
    }, [sceneOpen]);
    return (
        <Layout.Sider
            className='cvat-objects-sidebar'
            theme='light'
            width={330}
            collapsedWidth={0}
            reverseArrow
            collapsible
            trigger={null}
            collapsed={sidebarCollapsed}
        >
            {/* open: the toggle sits in the tab row; closed: it floats on the edge, since the sider has no width */}
            {sidebarCollapsed && (
                // eslint-disable-next-line
                <span
                    className={`cvat-objects-sidebar-sider
                        ant-layout-sider-zero-width-trigger
                        ant-layout-sider-zero-width-trigger-left`}
                    onClick={collapse}
                >
                    <MenuFoldOutlined title={t('Show')} />
                </span>
            )}

            {sceneOpen && <ScenePanel />}
            {/* the key remounts the tabs so defaultActiveKey applies after a mode switch */}
            <Tabs
                key={references ? 'references' : 'drawings'}
                type='line'
                defaultActiveKey={defaultTab}
                // hidden, not unmounted: the active tab survives the panel
                className={`cvat-objects-sidebar-tabs ${sceneOpen ? 'cvat-objects-sidebar-tabs-hidden' : ''}`}
                // narrower gaps keep the four tabs on one line in Russian too
                tabBarGutter={12}
                onChange={(key) => preview?.setActive(key === 'preview')}
                tabBarExtraContent={{
                    right: (
                        <Button
                            type='text'
                            size='small'
                            className='cvat-objects-sidebar-hide'
                            aria-label={t('Hide sidebar')}
                            icon={<MenuUnfoldOutlined />}
                            onClick={collapse}
                        />
                    ),
                }}
            >
                {hasSteps ? (
                    <Tabs.TabPane tab={t('Steps')} key='steps'>
                        <StepsPanel />
                    </Tabs.TabPane>
                ) : null}
                {references ? (
                    <Tabs.TabPane tab={t('Detections')} key='detections'>
                        <ReferencesPanel />
                    </Tabs.TabPane>
                ) : null}
                {/* a photo has its Detections tab instead */}
                {!references && preview ? (
                    <Tabs.TabPane tab={t('Preview')} key='preview'>
                        <PreviewPanelContainer />
                    </Tabs.TabPane>
                ) : null}
                <Tabs.TabPane forceRender tab={t('Objects')} key='objects'>
                    <ObjectsListContainer readonly={false} />
                </Tabs.TabPane>
                <Tabs.TabPane forceRender tab={t('Labels')} key='labels'>
                    <LabelsList />
                </Tabs.TabPane>

                {is2D && issuesCount > 0 ? (
                    <Tabs.TabPane tab={t('Issues {n}', { n: issuesCount })} key='issues'>
                        <IssuesListComponent />
                    </Tabs.TabPane>
                ) : null}
            </Tabs>

            {!sidebarCollapsed && !sceneOpen && <AppearanceBlock />}
        </Layout.Sider>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ObjectsSideBar));
